import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { 
  AlertTriangle, TrendingUp, Target, Brain, Lightbulb,
  CheckCircle2, XCircle, AlertCircle, BookOpen, Zap,
  ChevronRight, Award, BarChart2, RefreshCw, Sparkles,
  BookMarked, GraduationCap, FileQuestion, CheckCheck
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FormativeFeedback({ courseId }) {
  const [assessment, setAssessment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    console.log('FormativeFeedback: Component mounted, loading assessment...');
    loadAssessment();
  }, [courseId]);

  const loadAssessment = async () => {
    const token = localStorage.getItem('faheem_token');
    console.log('FormativeFeedback: Token exists:', !!token);
    
    if (!token) {
      console.log('FormativeFeedback: No token, setting loading to false');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      const url = courseId 
        ? `${BACKEND_URL}/api/formative-assessment/analyze?access_token=${token}&course_id=${courseId}`
        : `${BACKEND_URL}/api/formative-assessment/analyze?access_token=${token}`;
      
      console.log('FormativeFeedback: Fetching from:', url);
      const response = await axios.get(url);
      console.log('FormativeFeedback: Response received:', response.data);
      
      if (response.data) {
        setAssessment(response.data);
      }
    } catch (err) {
      console.error('FormativeFeedback: Failed to load assessment:', err);
      setError(err.message);
    } finally {
      console.log('FormativeFeedback: Setting loading to false');
      setLoading(false);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-50 text-red-700 border-red-200';
      case 'medium': return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'low': return 'bg-blue-50 text-blue-700 border-blue-200';
      default: return 'bg-slate-50 text-slate-700 border-slate-200';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      case 'low': return 'bg-blue-100 text-blue-700 border-blue-300';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return <AlertTriangle className="w-4 h-4" />;
      case 'medium': return <AlertCircle className="w-4 h-4" />;
      case 'low': return <Lightbulb className="w-4 h-4" />;
      default: return null;
    }
  };

  if (loading) {
    return (
      <Card className="border-slate-200" data-testid="formative-feedback-loading">
        <CardContent className="py-12">
          <div className="flex flex-col items-center justify-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p className="text-sm text-slate-500">Analyzing your performance...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!assessment || assessment.total_topics_attempted === 0) {
    return (
      <Card className="border-slate-200" data-testid="formative-feedback-empty">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Brain className="w-5 h-5 text-blue-600" />
            Formative Feedback Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-10" data-testid="formative-feedback-empty-state">
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-10 h-10 text-blue-300" />
            </div>
            <p className="text-slate-600 font-medium">No assessment data yet</p>
            <p className="text-sm text-slate-400 mt-1">Take some quizzes to receive personalized learning feedback</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="formative-feedback-card">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg" data-testid="formative-feedback-title">
            <Brain className="w-5 h-5 text-blue-600" />
            Formative Feedback Assessment
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={loadAssessment}
            className="text-slate-500 hover:text-blue-600"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        
        {/* Overall Performance Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center border border-blue-200">
            <BarChart2 className="w-6 h-6 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-700">{assessment.overall_performance}%</p>
            <p className="text-xs text-blue-600 font-medium">Overall</p>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-4 text-center border border-green-200">
            <CheckCircle2 className="w-6 h-6 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700">{assessment.topics_mastered}</p>
            <p className="text-xs text-green-600 font-medium">Mastered</p>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl p-4 text-center border border-yellow-200">
            <AlertCircle className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-yellow-700">{assessment.topics_need_work}</p>
            <p className="text-xs text-yellow-600 font-medium">Need Work</p>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 text-center border border-purple-200">
            <BookOpen className="w-6 h-6 text-purple-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-700">{assessment.total_topics_attempted}</p>
            <p className="text-xs text-purple-600 font-medium">Topics</p>
          </div>
        </div>

        {/* Learning Recommendations */}
        {assessment.learning_recommendations && assessment.learning_recommendations.length > 0 && (
          <div className="bg-gradient-to-r from-slate-50 via-blue-50/50 to-purple-50/50 rounded-xl p-5 border border-slate-200">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              Personalized Learning Recommendations
            </h3>
            <div className="space-y-3">
              {assessment.learning_recommendations.map((rec, idx) => (
                <div key={idx} className="flex items-start gap-3 text-sm text-slate-700 bg-white/60 rounded-lg p-3 border border-slate-100">
                  <ChevronRight className="w-4 h-4 mt-0.5 text-blue-500 flex-shrink-0" />
                  <span>{rec}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Weakness Patterns with Detailed Feedback */}
        {assessment.weaknesses && assessment.weaknesses.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-red-500" />
              Areas Needing Focus
              <Badge variant="outline" className="ml-2 text-red-600 border-red-300">
                {assessment.weaknesses.length} topics
              </Badge>
            </h3>
            
            <Accordion type="multiple" className="space-y-3">
              {assessment.weaknesses.map((weakness, idx) => (
                <AccordionItem 
                  key={idx}
                  value={`weakness-${idx}`}
                  className={`rounded-xl border-2 ${getPriorityColor(weakness.priority)} overflow-hidden`}
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-2">
                      <div className="flex items-center gap-3">
                        {getPriorityIcon(weakness.priority)}
                        <div className="text-left">
                          <span className="font-semibold">{weakness.topic_name}</span>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={`text-xs ${getPriorityBadgeColor(weakness.priority)}`}>
                              {weakness.priority} priority
                            </Badge>
                            <span className="text-xs opacity-70">
                              {weakness.attempts_count} attempt{weakness.attempts_count !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="text-right mr-2">
                        <span className="text-2xl font-bold">{weakness.average_score}%</span>
                        <p className="text-xs opacity-70">avg score</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      {/* Progress bar */}
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span>Progress to Mastery</span>
                          <span>Best: {weakness.best_score}%</span>
                        </div>
                        <Progress value={weakness.average_score} className="h-2" />
                      </div>

                      {/* AI Recommendation */}
                      {weakness.detailed_feedback?.ai_recommendation && (
                        <div className="bg-white/80 rounded-lg p-4 border border-slate-200">
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <Sparkles className="w-4 h-4 text-purple-500" />
                            FaheemGPT Study Recommendation
                          </h4>
                          <p className="text-sm text-slate-700 leading-relaxed">
                            {weakness.detailed_feedback.ai_recommendation}
                          </p>
                        </div>
                      )}

                      {/* Specific Study Areas */}
                      {weakness.detailed_feedback?.specific_study_areas?.length > 0 && (
                        <div className="bg-white/80 rounded-lg p-4 border border-slate-200">
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <BookMarked className="w-4 h-4 text-blue-500" />
                            Concepts to Review
                          </h4>
                          <div className="flex flex-wrap gap-2">
                            {weakness.detailed_feedback.specific_study_areas.map((area, i) => (
                              <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                {area}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Missed Questions */}
                      {weakness.detailed_feedback?.missed_concepts?.length > 0 && (
                        <div className="bg-white/80 rounded-lg p-4 border border-red-100">
                          <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <XCircle className="w-4 h-4 text-red-500" />
                            Questions You Missed
                            <Badge variant="outline" className="ml-auto text-red-600 border-red-300 text-xs">
                              {weakness.detailed_feedback.missed_concepts.length} questions
                            </Badge>
                          </h4>
                          <div className="space-y-3">
                            {weakness.detailed_feedback.missed_concepts.map((mc, i) => (
                              <div key={i} className="bg-red-50/50 rounded-lg p-3 border border-red-100">
                                <p className="text-sm text-slate-800 font-medium mb-2">
                                  <FileQuestion className="w-4 h-4 inline mr-1 text-slate-500" />
                                  {mc.question}
                                </p>
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className="bg-red-100/80 rounded px-2 py-1.5">
                                    <span className="text-red-600 font-medium">Your answer: </span>
                                    <span className="text-red-700">{mc.student_answer}</span>
                                  </div>
                                  <div className="bg-green-100/80 rounded px-2 py-1.5">
                                    <span className="text-green-600 font-medium">Correct: </span>
                                    <span className="text-green-700">{mc.correct_answer}</span>
                                  </div>
                                </div>
                                <div className="mt-2 flex items-center gap-1">
                                  <Badge variant="outline" className="text-xs bg-slate-50 text-slate-600 border-slate-200">
                                    Concept: {mc.concept}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Mastered Questions (what they got right) */}
                      {weakness.detailed_feedback?.mastered_concepts?.length > 0 && (
                        <div className="bg-white/80 rounded-lg p-4 border border-green-100">
                          <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <CheckCheck className="w-4 h-4 text-green-500" />
                            Questions You Got Right
                            <Badge variant="outline" className="ml-auto text-green-600 border-green-300 text-xs">
                              {weakness.detailed_feedback.mastered_concepts.length} questions
                            </Badge>
                          </h4>
                          <div className="space-y-2">
                            {weakness.detailed_feedback.mastered_concepts.map((mc, i) => (
                              <div key={i} className="bg-green-50/50 rounded-lg p-2 border border-green-100 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm text-slate-700">{mc.question}</p>
                                  <Badge variant="outline" className="text-xs mt-1 bg-green-50 text-green-600 border-green-200">
                                    {mc.concept}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

        {/* Strength Patterns */}
        {assessment.strengths && assessment.strengths.length > 0 && (
          <div>
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-green-500" />
              Your Strengths
              <Badge variant="outline" className="ml-2 text-green-600 border-green-300">
                {assessment.strengths.length} topics
              </Badge>
            </h3>
            
            <Accordion type="multiple" className="space-y-3">
              {assessment.strengths.map((strength, idx) => (
                <AccordionItem 
                  key={idx}
                  value={`strength-${idx}`}
                  className="rounded-xl border-2 bg-green-50 border-green-200 overflow-hidden"
                >
                  <AccordionTrigger className="px-4 py-3 hover:no-underline">
                    <div className="flex items-center justify-between w-full pr-2">
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className="w-5 h-5 text-green-600" />
                        <div className="text-left">
                          <span className="font-semibold text-green-800">{strength.topic_name}</span>
                          <p className="text-xs text-green-600 mt-1">
                            {strength.attempts_count} attempt{strength.attempts_count !== 1 ? 's' : ''} • Mastery achieved
                          </p>
                        </div>
                      </div>
                      <div className="text-right mr-2">
                        <span className="text-2xl font-bold text-green-700">{strength.average_score}%</span>
                        <p className="text-xs text-green-600">mastery</p>
                      </div>
                    </div>
                  </AccordionTrigger>
                  
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4">
                      <Progress value={strength.mastery_score} className="h-2" />
                      
                      {/* AI Recommendation for strengths */}
                      {strength.detailed_feedback?.ai_recommendation && (
                        <div className="bg-white/80 rounded-lg p-4 border border-green-200">
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <GraduationCap className="w-4 h-4 text-green-500" />
                            Keep It Up!
                          </h4>
                          <p className="text-sm text-slate-700 leading-relaxed">
                            {strength.detailed_feedback.ai_recommendation}
                          </p>
                        </div>
                      )}

                      {/* Mastered concepts */}
                      {strength.detailed_feedback?.mastered_concepts?.length > 0 && (
                        <div className="bg-white/80 rounded-lg p-4 border border-green-200">
                          <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                            <CheckCheck className="w-4 h-4 text-green-500" />
                            Concepts You've Mastered
                          </h4>
                          <div className="space-y-2">
                            {strength.detailed_feedback.mastered_concepts.map((mc, i) => (
                              <div key={i} className="bg-green-50 rounded-lg p-2 border border-green-100 flex items-start gap-2">
                                <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                                <div>
                                  <p className="text-sm text-slate-700">{mc.question}</p>
                                  <Badge variant="outline" className="text-xs mt-1 bg-green-100 text-green-700 border-green-200">
                                    {mc.concept}
                                  </Badge>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Any missed questions even in strengths */}
                      {strength.detailed_feedback?.missed_concepts?.length > 0 && (
                        <div className="bg-white/80 rounded-lg p-4 border border-yellow-200">
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4 text-yellow-500" />
                            Room for Improvement
                          </h4>
                          <p className="text-xs text-slate-500 mb-2">
                            Even though you've mastered this topic, here are a few questions to review:
                          </p>
                          <div className="space-y-2">
                            {strength.detailed_feedback.missed_concepts.slice(0, 2).map((mc, i) => (
                              <div key={i} className="bg-yellow-50/50 rounded-lg p-2 border border-yellow-100 text-sm">
                                <p className="text-slate-700">{mc.question}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  Review: <span className="text-yellow-700">{mc.concept}</span>
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}

      </CardContent>
    </Card>
  );
}
